import { useCallback, useEffect, useState } from "react";

import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  Block as VoidIcon,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import { PageContainer } from "@/features/ui";
import { PDFViewer } from "@react-pdf/renderer";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import {
  SalesOrderDeliveryStatusEnum,
  SalesOrderInvoiceStatusEnum,
  SalesOrderPdf,
  SalesOrderStatusEnum,
  SalesOrderVoidConfirmationDialog,
} from "@/features/sales-orders";
import {
  GeneralProductSection,
  LensProductSection,
  TransactionInformationSection,
} from "@/features/sales-orders/sales-order";
import { apiOutletClient, formatMoney, getErrorMessage } from "@/features/shared";
import { CenteredLoading, ResponsiveToolbarButton } from "@/features/ui";

export const SalesOrder = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { outlet, hasPermission, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [salesOrder, setSalesOrder] = useState(null);
  const [error, setError] = useState(null);
  const [voidConfirmationDialogOpen, setVoidConfirmationDialogOpen] =
    useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  const loadSalesOrder = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`sales-orders/${referenceNumber}`, {
        params: {
          withTransactionHistories: true,
          withCreator: true,
          withAvailableDownPaymentAmount: true,
        },
      });
      setSalesOrder(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memuat pesanan penjualan")
      );
    } finally {
      setLoading(false);
    }
  }, [outlet, referenceNumber]);

  useEffect(() => {
    loadSalesOrder();
  }, [loadSalesOrder]);

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  const canEdit =
    hasPermission(PermissionEnum.UPDATE_SALES_ORDERS) &&
    salesOrder.buyerableType === "OUTLET" &&
    salesOrder.status !== SalesOrderStatusEnum.VOID &&
    salesOrder.invoiceStatus !==
      SalesOrderInvoiceStatusEnum.FULLY_INVOICED &&
    salesOrder.deliveryStatus !==
      SalesOrderDeliveryStatusEnum.FULLY_DELIVERED;

  const canVoid =
    hasPermission(PermissionEnum.VOID_SALES_ORDERS) &&
    salesOrder.status !== SalesOrderStatusEnum.VOID &&
    dayjs(salesOrder.transactionDate).isAfter(
      dayjs().subtract(3, "day"),
      "day"
    );

  const toolbarContent = (
    <>
      {canEdit && (
        <ResponsiveToolbarButton
          label="Ubah"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate("edit")}
        />
      )}
      <ResponsiveToolbarButton
        label="Cetak PDF"
        variant="outlined"
        startIcon={<PdfIcon />}
        onClick={() => setPdfDialogOpen(true)}
      />
      {canVoid && (
        <ResponsiveToolbarButton
          label="Void"
          variant="contained"
          color="error"
          startIcon={<VoidIcon />}
          onClick={() => setVoidConfirmationDialogOpen(true)}
        />
      )}
    </>
  );

  return (
    <PageContainer
      maxWidth="xl"
      title="Pesanan Penjualan"
      breadcrumbs={[
        { title: "Pesanan Penjualan", path: "/sales/sales-orders" },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <TransactionInformationSection salesOrder={salesOrder} />
        {salesOrder.salesOrderPayments.length > 0 && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1">Metode Pembayaran</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                {salesOrder.salesOrderPayments.map((payment, index) => (
                  <Box key={payment.id} sx={{ display: "flex" }}>
                    <Typography>
                      {index + 1}. {payment.paymentMethod.name}
                    </Typography>
                    <Typography sx={{
                      ml: 1
                    }}>
                      ({formatMoney(payment.paymentAmount)})
                    </Typography>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </Paper>
        )}
        {salesOrder.salesOrderGeneralProductVariants.length > 0 && (
          <GeneralProductSection
            salesOrderGeneralProductVariants={
              salesOrder.salesOrderGeneralProductVariants
            }
            warehouse={salesOrder.warehouse}
          />
        )}
        {salesOrder.salesOrderLensProducts.length > 0 && (
          <LensProductSection
            salesOrderLensProducts={salesOrder.salesOrderLensProducts}
          />
        )}

        {voidConfirmationDialogOpen && (
          <SalesOrderVoidConfirmationDialog
            referenceNumber={salesOrder.referenceNumber}
            onClose={() => setVoidConfirmationDialogOpen(false)}
            onSubmitted={loadSalesOrder}
          />
        )}

        {/* PDF Dialog */}
        <Dialog
          open={pdfDialogOpen}
          onClose={() => setPdfDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          slotProps={{
            paper: {
              sx: { height: "90vh" },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Pesanan Penjualan - {referenceNumber}
            <IconButton onClick={() => setPdfDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: "100%" }}>
            {salesOrder && user && (
              <PDFViewer width="100%" height="100%">
                <SalesOrderPdf
                  salesOrder={{
                    ...salesOrder,
                    salesOrderGeneralProductVariants: [
                      ...salesOrder.salesOrderGeneralProductVariants,
                    ].sort((a, b) => a.name.localeCompare(b.name)),
                  }}
                />
              </PDFViewer>
            )}
          </DialogContent>
        </Dialog>
      </Stack>
    </PageContainer>
  );
};
